import React, { useRef, useState } from 'react';
import { ArrowRight, CheckCircle2, Clock3, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { submitPartnerApplication } from '../services/api';
import styles from './PartnerApplicationForm.module.scss';

const initialFormData = {
  name: '',
  email: '',
  company: '',
  role: '',
  partner_type: '',
  market: '',
  profile_url: '',
  network_description: '',
  fit_reason: '',
  website: '',
  consent: false,
};

const firstApiError = (payload) => {
  if (!payload) return '';
  if (payload.detail) return payload.detail;
  const fieldError = Object.values(payload).find((value) => Array.isArray(value) && value.length);
  return fieldError?.[0] || '';
};

const PartnerApplicationForm = () => {
  const { t } = useTranslation('partners');
  const [formData, setFormData] = useState(initialFormData);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [reference, setReference] = useState('');
  const startedAt = useRef(Date.now() / 1000);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus('sending');
    setError('');

    try {
      const response = await submitPartnerApplication({
        ...formData,
        started_at: startedAt.current,
      });
      setReference(response.data.reference);
      setStatus('success');
    } catch (submitError) {
      const message = firstApiError(submitError.response?.data)
        || t('applicationForm.form.error');
      setError(message);
      setStatus('idle');
    }
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setReference('');
    setError('');
    startedAt.current = Date.now() / 1000;
    setStatus('idle');
  };

  return (
    <section id="partner-application" className={styles.applicationSection} aria-labelledby="partner-application-title">
      <div className={styles.applicationContainer}>
        <div className={styles.applicationIntro}>
          <div className={styles.eyebrow}>{t('applicationForm.eyebrow')}</div>
          <h2 id="partner-application-title">{t('applicationForm.title')}</h2>
          <p>{t('applicationForm.subtitle')}</p>

          <div className={styles.expectations}>
            <div className={styles.expectationItem}>
              <Clock3 size={18} />
              <span>{t('applicationForm.time')}</span>
            </div>
            <div className={styles.expectationItem}>
              <ShieldCheck size={18} />
              <span>{t('applicationForm.privacy')}</span>
            </div>
          </div>
        </div>

        {status === 'success' ? (
          <div className={styles.successCard} role="status" aria-live="polite">
            <div className={styles.successIcon}><CheckCircle2 size={32} /></div>
            <div>
              <div className={styles.successEyebrow}>{t('applicationForm.success.eyebrow')}</div>
              <h3>{t('applicationForm.success.title')}</h3>
              <p>{t('applicationForm.success.text')}</p>
              {reference && <p className={styles.reference}>{t('applicationForm.success.reference', { reference })}</p>}
              <button type="button" className={styles.resetButton} onClick={resetForm}>
                {t('applicationForm.success.again')}
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.formCard}>
            <div className={styles.formHeader}>
              <div>
                <div className={styles.formEyebrow}>{t('applicationForm.form.eyebrow')}</div>
                <h3>{t('applicationForm.form.title')}</h3>
              </div>
              <span className={styles.requiredNote}>{t('applicationForm.form.required')}</span>
            </div>

            <form onSubmit={handleSubmit}>
              <input
                className={styles.honeypot}
                type="text"
                name="website"
                value={formData.website}
                onChange={handleChange}
                tabIndex="-1"
                autoComplete="off"
                aria-hidden="true"
              />

              {error && <div className={styles.formError} role="alert">{error}</div>}

              <div className={styles.fieldGrid}>
                <label className={styles.field}>
                  <span>{t('applicationForm.form.name')} <b>*</b></span>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder={t('applicationForm.form.namePlaceholder')} autoComplete="name" maxLength="150" required />
                </label>
                <label className={styles.field}>
                  <span>{t('applicationForm.form.email')} <b>*</b></span>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder={t('applicationForm.form.emailPlaceholder')} autoComplete="email" required />
                </label>
              </div>

              <div className={styles.fieldGrid}>
                <label className={styles.field}>
                  <span>{t('applicationForm.form.company')} <b>*</b></span>
                  <input type="text" name="company" value={formData.company} onChange={handleChange} placeholder={t('applicationForm.form.companyPlaceholder')} autoComplete="organization" maxLength="150" required />
                </label>
                <label className={styles.field}>
                  <span>{t('applicationForm.form.role')} <b>*</b></span>
                  <input type="text" name="role" value={formData.role} onChange={handleChange} placeholder={t('applicationForm.form.rolePlaceholder')} maxLength="120" required />
                </label>
              </div>

              <div className={styles.fieldGrid}>
                <label className={styles.field}>
                  <span>{t('applicationForm.form.partnerType')} <b>*</b></span>
                  <select name="partner_type" value={formData.partner_type} onChange={handleChange} required>
                    <option value="">{t('applicationForm.form.partnerTypePlaceholder')}</option>
                    <option value="agency_consultant">{t('applicationForm.form.partnerTypeOptions.agency')}</option>
                    <option value="community">{t('applicationForm.form.partnerTypeOptions.community')}</option>
                    <option value="hospitality_tourism">{t('applicationForm.form.partnerTypeOptions.hospitality')}</option>
                    <option value="creator_network">{t('applicationForm.form.partnerTypeOptions.network')}</option>
                    <option value="other">{t('applicationForm.form.partnerTypeOptions.other')}</option>
                  </select>
                </label>
                <label className={styles.field}>
                  <span>{t('applicationForm.form.market')} <b>*</b></span>
                  <input type="text" name="market" value={formData.market} onChange={handleChange} placeholder={t('applicationForm.form.marketPlaceholder')} maxLength="150" required />
                </label>
              </div>

              <label className={styles.field}>
                <span>{t('applicationForm.form.profileUrl')}</span>
                <input type="url" name="profile_url" value={formData.profile_url} onChange={handleChange} placeholder={t('applicationForm.form.profileUrlPlaceholder')} autoComplete="url" maxLength="500" />
              </label>

              <label className={styles.field}>
                <span>{t('applicationForm.form.network')} <b>*</b></span>
                <textarea name="network_description" value={formData.network_description} onChange={handleChange} placeholder={t('applicationForm.form.networkPlaceholder')} rows="4" maxLength="2000" required />
              </label>

              <label className={styles.field}>
                <span>{t('applicationForm.form.fit')} <b>*</b></span>
                <textarea name="fit_reason" value={formData.fit_reason} onChange={handleChange} placeholder={t('applicationForm.form.fitPlaceholder')} rows="3" maxLength="1200" required />
              </label>

              <label className={styles.consentField}>
                <input type="checkbox" name="consent" checked={formData.consent} onChange={handleChange} required />
                <span>{t('applicationForm.form.consent')} <a href="/privacy-policy">{t('applicationForm.form.privacyLink')}</a> <b>*</b></span>
              </label>

              <button type="submit" className={styles.submitButton} disabled={status === 'sending'}>
                {status === 'sending' ? t('applicationForm.form.submitting') : t('applicationForm.form.submit')}
                {status !== 'sending' && <ArrowRight size={18} />}
              </button>
              <p className={styles.formFootnote}>{t('applicationForm.form.footnote')}</p>
            </form>
          </div>
        )}
      </div>
    </section>
  );
};

export default PartnerApplicationForm;
